import React, { useState } from "react";
import { restaurantInfo } from "../data/mock";

/**
 * Side-by-side logo comparison. Each variant is previewed at three real
 * usage sizes and against both a dark background (nav / hero / card front)
 * and a cream background (menu / card back). Click a variant to select it —
 * a snippet with the file path shows what the owner should tell me to
 * bake into the production site.
 */

const VARIANTS = [
  {
    id: "original",
    label: "Your original",
    file: "/media/logo-300.png",
    blurb: "The logo you uploaded, background-cleaned. No AI modification.",
  },
  {
    id: "a",
    label: "A · Refined",
    file: "/media/logo-variants/a-refined.jpeg",
    blurb: "Same subject as your original, professionally re-illustrated: crisper lines, refined proportions, elegant dashed banner, warm-red script tagline. Closest to what you have — polished.",
  },
  {
    id: "b",
    label: "B · Modern minimal",
    file: "/media/logo-variants/b-minimal.jpeg",
    blurb: "Simplified to essential shapes. Three-colour palette, no banner, clean modern sans-serif wordmark. Timeless and scalable — great for signage & merch.",
  },
  {
    id: "c",
    label: "C · Editorial badge",
    file: "/media/logo-variants/c-fresh.jpeg",
    blurb: "Circular emblem with West-African motifs. Feels like it belongs on a magazine cover or storefront window sticker. Rich, distinctive, warm.",
  },
  {
    id: "d",
    label: "D · Icon mark",
    file: "/media/logo-variants/d-monogram.jpeg",
    blurb: "Simplified icon-only mark. No text. Best used *alongside* one of A / B / C — perfect for navbar, favicon, app icon, social avatars.",
  },
];

function ContextTile({ dark, size, variant, label }) {
  return (
    <div className="ctx-tile">
      <div className="ctx-label">{label}</div>
      <div className={`ctx-box ${dark ? "ctx-dark" : "ctx-cream"}`} style={{ minHeight: size + 40 }}>
        <img src={variant.file} alt={variant.label} style={{ height: `${size}px`, width: "auto", maxWidth: "100%" }} />
      </div>
    </div>
  );
}

export default function LogoPicker() {
  const [selected, setSelected] = useState("a");

  return (
    <div className="picker-shell">
      <PickerStyles />
      <header className="picker-header">
        <div>
          <h1>Logo picker</h1>
          <p>Compare all variants against real usage. Click your favourite and tell me the letter — I&apos;ll bake it into the whole site.</p>
        </div>
      </header>

      {VARIANTS.map((v) => {
        const isSel = selected === v.id;
        return (
          <div
            key={v.id}
            className={`variant-card ${isSel ? "variant-card-selected" : ""}`}
            data-testid={`variant-${v.id}`}
          >
            <div className="variant-head">
              <div>
                <h2 className="variant-title">
                  {v.label}
                  {isSel && <span className="variant-selected-tag">selected</span>}
                </h2>
                <p className="variant-blurb">{v.blurb}</p>
              </div>
              <button
                onClick={() => setSelected(v.id)}
                className={`variant-pick ${isSel ? "variant-pick-active" : ""}`}
                data-testid={`pick-${v.id}`}
              >
                {isSel ? "✓ Chosen" : "Pick this one"}
              </button>
            </div>

            <div className="variant-full">
              <img src={v.file} alt={v.label} className="variant-full-img" />
            </div>

            <div className="ctx-row">
              <ContextTile variant={v} dark size={90} label="Menu hero / cream card" />
              <ContextTile variant={v} size={90} label="Business card front / dark" />
              <ContextTile variant={v} dark size={44} label="Navbar (small)" />
              <ContextTile variant={v} dark size={24} label="Favicon" />
            </div>
          </div>
        );
      })}

      <footer className="picker-foot">
        <p>
          Reply with your choice — e.g. <code>use variant B</code> or <code>combine B + D</code>. I&apos;ll swap the site logo, favicon, business card, and physical menu to match.
        </p>
      </footer>
    </div>
  );
}

function PickerStyles() {
  return (
    <style>{`
      .picker-shell {
        min-height: 100vh; background: #0f0b09; color: #e4dcc9;
        padding: 40px 20px 80px; font-family: -apple-system, "Segoe UI", sans-serif;
      }
      .picker-header {
        max-width: 900px; margin: 0 auto 40px;
      }
      .picker-header h1 {
        font-family: Georgia, serif; font-style: italic;
        font-size: 42px; color: #f6ebd0; margin: 0 0 8px;
      }
      .picker-header p { color: #a09484; margin: 0; font-size: 14px; }

      .variant-card {
        max-width: 900px; margin: 0 auto 32px;
        background: #1a1512; border: 1px solid #3a3229;
        border-radius: 6px; overflow: hidden; transition: border-color 0.2s;
      }
      .variant-card-selected {
        border-color: #B7853D;
        box-shadow: 0 0 0 1px #B7853D, 0 8px 40px rgba(183, 133, 61, 0.15);
      }
      .variant-head {
        display: flex; justify-content: space-between; align-items: flex-start;
        gap: 20px; padding: 24px 28px; border-bottom: 1px solid #2a221c;
      }
      .variant-title {
        font-family: Georgia, serif; font-size: 24px;
        color: #f6ebd0; margin: 0 0 6px;
      }
      .variant-selected-tag {
        display: inline-block; margin-left: 12px;
        font-size: 10px; letter-spacing: 3px; text-transform: uppercase;
        color: #B7853D; padding: 3px 8px; border: 1px solid #B7853D;
        border-radius: 20px; vertical-align: middle;
      }
      .variant-blurb {
        color: #a09484; margin: 0; font-size: 13px; line-height: 1.5;
        max-width: 620px;
      }
      .variant-pick {
        flex-shrink: 0; background: transparent; color: #B7853D;
        border: 1px solid #B7853D; padding: 8px 16px; font-size: 12px;
        letter-spacing: 1px; text-transform: uppercase; cursor: pointer;
        border-radius: 3px; font-weight: 600; transition: all 0.2s;
      }
      .variant-pick:hover { background: rgba(183, 133, 61, 0.1); }
      .variant-pick-active {
        background: #B7853D; color: #1a1512;
      }

      .variant-full {
        padding: 32px; background:
          repeating-conic-gradient(#252025 0% 25%, #1a1512 0% 50%) 50% / 20px 20px;
        display: flex; align-items: center; justify-content: center;
      }
      .variant-full-img {
        max-width: 100%; max-height: 340px; object-fit: contain;
      }

      .ctx-row {
        display: grid; grid-template-columns: repeat(4, 1fr);
        gap: 1px; background: #2a221c;
      }
      @media (max-width: 720px) {
        .ctx-row { grid-template-columns: repeat(2, 1fr); }
      }
      .ctx-tile { background: #1a1512; padding: 14px; }
      .ctx-label {
        font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase;
        color: #7a6f5f; margin-bottom: 10px;
      }
      .ctx-box {
        display: flex; align-items: center; justify-content: center;
        border-radius: 3px; padding: 8px;
      }
      .ctx-dark { background: #0f0b09; }
      .ctx-cream { background: #f6ebd0; }

      .picker-foot {
        max-width: 900px; margin: 40px auto 0;
        padding: 20px; border-top: 1px solid #3a3229;
        color: #a09484; font-size: 13px;
      }
      .picker-foot code {
        color: #B7853D; background: rgba(183, 133, 61, 0.1);
        padding: 2px 6px; border-radius: 3px;
      }
    `}</style>
  );
}
